import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { intlService } from '~/i18n/IntlService';

import { getGroupPermission } from './getGroupPermission';

const getPermissionsResponse = () => {
  const intl = intlService.getIntl();
  const hasPlaybooks = getFlag(FlagType.Playbooks);
  const hasUnifiedFieldsM1 = getFlag(FlagType.UnifiedFieldsM1);
  const groupPermissionGroup = getGroupPermission();

  return {
    data: [
      {
        type: 'permission_group',
        id: '0e2a99d1-03a6-4f10-8df2-9a28a30e1c16',
        attributes: {
          name: intl.formatMessage({
            id: 'GetPermissionsResponse.documentsGroupName',
            defaultMessage: 'Documents',
            description: 'Permission group title for Documents',
          }),
          permissions: [
            {
              id: '03656c72-5368-4d51-9e0d-008d54b1381e',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.allDocumentsAccessName',
                defaultMessage: 'All Documents: Access',
                description: 'Permission name for full access to documents',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.allDocumentsAccessDescription',
                defaultMessage:
                  'Edit, view, delete, and download documents subject to folder access control settings. Can also access Search (save & share searches), and Alerts.',
                description:
                  'Explains capabilities granted by All Documents: Access',
              }),
              feature: 'documents',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: 'f049f264-d14a-4ef3-9958-fc1932e43f12',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.folderAccessManageName',
                defaultMessage: 'Folder Access: Manage',
                description: 'Permission name for managing folder access',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.folderAccessManageDescription',
                defaultMessage:
                  'Allows users to modify folder access settings, modify view/edit access for other users and departments. Note: this permission also requires the All Documents: Access permission to be granted to manage Folder access settings.',
                description:
                  'Explains capabilities for managing folder access; note about dependency on All Documents: Access',
              }),
              feature: 'folder_access',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
          ],
        },
      },
      ...groupPermissionGroup,
      {
        type: 'permission_group',
        id: '00aa9601-2382-470d-a685-d203583623e1',
        attributes: {
          name: intl.formatMessage({
            id: 'GetPermissionsResponse.dashboardsGroupName',
            defaultMessage: 'Dashboards',
            description: 'Permission group title for Dashboards',
          }),
          permissions: [
            {
              id: 'b5bd361c-5c60-4c5e-9ef5-3b73a086e002',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.documentsDashboardsManageName',
                defaultMessage: 'Documents Dashboards: Manage',
                description:
                  'Permission name for managing documents dashboards',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.documentsDashboardsManageDescription',
                defaultMessage:
                  'Create, view, edit, share or delete document dashboards. Can also view or edit individual files subject to folder access control settings.',
                description:
                  'Explains capabilities for managing document dashboards',
              }),
              feature: 'documents_dashboard',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '3be632ad-3ba6-48ea-a6fb-033b14d071e5',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.workflowDashboardsManageName',
                defaultMessage: 'Workflow Dashboards: Manage',
                description: 'Permission name for managing workflow dashboards',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.workflowDashboardsManageDescription',
                defaultMessage:
                  'Create, view, edit, share or delete workflow dashboards. Can also view or edit individual workflow tickets subject to workflow access control settings.',
                description:
                  'Explains capabilities for managing workflow dashboards',
              }),
              feature: 'workflows_dashboard',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
          ],
        },
      },
      {
        type: 'permission_group',
        id: 'f580239a-93dd-4f94-83c4-6a4d6aa9f6d4',
        attributes: {
          name: intl.formatMessage({
            id: 'GetPermissionsResponse.workflowGroupName',
            defaultMessage: 'Workflow',
            description: 'Permission group title for Workflow',
          }),
          permissions: [
            {
              id: '16fbec88-790c-4226-a172-3c8138cbcf20',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.workflowBuilderManageName',
                defaultMessage: 'Workflow Builder: Manage',
                description: 'Permission name for managing workflow builder',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.workflowBuilderManageDescription',
                defaultMessage: 'Create, view, edit, or delete Workflows.',
                description: 'Explains capabilities for managing workflows',
              }),
              feature: 'workflow_templates',
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '1f2e9eb9-30fb-4b4c-b4b4-4d4d4a8f4c4a',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.ticketsAccessName',
                defaultMessage: 'Tickets: Access',
                description: 'Permission name for accessing workflow tickets',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.ticketsAccessDescription',
                defaultMessage:
                  'Create, view, edit, or delete Workflow Tickets.',
                description:
                  'Explains capabilities for accessing workflow tickets',
              }),
              feature: 'workflow_tickets',
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '72917b19-c2fa-4d63-97eb-9c6d04f6c7bc',
              name: hasPlaybooks
                ? intl.formatMessage({
                    id: 'GetPermissionsResponse.libraryManagePermission',
                    defaultMessage: 'Libraries: Manage',
                    description:
                      'Permission name for accessing library management',
                  })
                : intl.formatMessage({
                    id: 'GetPermissionsResponse.clauseLibraryManageName',
                    defaultMessage: 'Clause Library: Manage',
                    description: 'Permission name for managing clause library',
                  }),
              description: hasPlaybooks
                ? intl.formatMessage({
                    id: 'GetPermissionsResponse.libraryManageAiInstructionDescription',
                    defaultMessage:
                      'Create, edit, and delete content across Libraries (clauses, playbooks, AI instructions).',
                    description: 'Explains capabilities for managing libraries',
                  })
                : intl.formatMessage({
                    id: 'GetPermissionsResponse.clauseLibraryManageDescription',
                    defaultMessage:
                      'Create, view, edit, or delete clauses in the Clause Library.',
                    description:
                      'Explains capabilities for managing clauses in the clause library',
                  }),
              feature: 'clause_library',
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '6b7d0b8f-14c5-482a-9f7e-b8ba2cc5c5e1',
              name: hasPlaybooks
                ? intl.formatMessage({
                    id: 'GetPermissionsResponse.libraryViewPermission',
                    defaultMessage: 'Libraries: View',
                    description:
                      'Permission name for accessing library viewing',
                  })
                : intl.formatMessage({
                    id: 'GetPermissionsResponse.clauseLibraryViewName',
                    defaultMessage: 'Clause Library: View',
                    description: 'Permission name for viewing clause library',
                  }),
              description: hasPlaybooks
                ? intl.formatMessage({
                    id: 'GetPermissionsResponse.libraryViewAiInstructionDescription',
                    defaultMessage:
                      'View library content (clauses, playbooks, AI instructions).',
                    description:
                      'Explains capabilities for viewing the libraries',
                  })
                : intl.formatMessage({
                    id: 'GetPermissionsResponse.clauseLibraryViewDescription',
                    defaultMessage: 'View the Clause Library.',
                    description:
                      'Explains capabilities for viewing the clause library',
                  }),
              feature: 'clause_library',
              relations: ['feature_viewer'],
            },
          ],
        },
      },
      {
        type: 'permission_group',
        id: 'c2b75206-3c03-4293-a0a6-0e8afdd1fda1',
        attributes: {
          name: intl.formatMessage({
            id: 'GetPermissionsResponse.aiToolsGroupName',
            defaultMessage: 'AI Tools',
            description: 'Permission group title for AI Tools',
          }),
          permissions: [
            {
              id: '66b31814-5ef7-4e4c-9f68-647475f6f869',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.clauseAiManageName',
                defaultMessage: 'Clause AI: Manage',
                description: 'Permission name for managing Clause AI',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.clauseAiManageDescription',
                defaultMessage:
                  'Create clause samples in Automation hub and use Quick AI to tag clauses in documents; Suspend and delete clause samples in Automation hub.',
                description:
                  'Explains capabilities for Clause AI including creating, tagging, suspending, and deleting clause samples',
              }),
              feature: 'clause_samples',
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '70468eca-8cb0-4893-bbd5-2483e906b26e',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.customAIManageName',
                defaultMessage: 'Custom AI: Manage',
                description: 'Permission name for managing Custom AI',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.customAIManageDescription',
                defaultMessage:
                  'Create and manage Custom AI models to track information in documents.',
                description:
                  'Explains capabilities for managing Custom AI and data fields',
              }),
              feature: 'document_x_ray',
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '83d7d149-d84f-4154-b666-e3f416a8d499',
              name: hasPlaybooks
                ? intl.formatMessage({
                    id: 'GetPermissionsResponse.aiReviewEditingAccessName',
                    defaultMessage: 'AI Review & Editing: Access',
                    description:
                      'Permission name for AI Review and editing tools access',
                  })
                : intl.formatMessage({
                    id: 'GetPermissionsResponse.aiDraftingToolsEditName',
                    defaultMessage: 'AI Drafting Tools: Edit',
                    description:
                      'Permission name for using AI Drafting Tools while editing documents',
                  }),
              description: hasPlaybooks
                ? intl.formatMessage({
                    id: 'GetPermissionsResponse.aiReviewEditingAccessDescription',
                    defaultMessage:
                      'Run AI Review against Playbooks and use AI editing tools (redlining, clause suggestions) when working in documents. Requires Tickets: Access.',
                    description:
                      'Explains capabilities for AI Review and editing tools with Tickets: Access dependency',
                  })
                : intl.formatMessage({
                    id: 'GetPermissionsResponse.aiDraftingToolsEditDescription',
                    defaultMessage:
                      'Use Automated Redlining and Clause Creator when editing a document. Note: this permission also requires the Tickets: Access permission to be granted to use the AI Drafting Tools functionality.',
                    description:
                      'Explains capabilities for AI Drafting Tools and dependency on Tickets: Access',
                  }),
              feature: 'ai_drafting_tools',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '583c61a8-02a4-4d21-9cb8-792f1c5bd7e5',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.conversationalAiAccessName',
                defaultMessage: 'Conversational AI:  Access',
                description: 'Permission name for accessing Conversational AI',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.conversationalAiAccessDescription',
                defaultMessage:
                  'Engage with Evisort AI to retrieve information about a document or set of documents. Note: this permission also requires at least one of the following permissions All Documents: Access, Documents Dashboards: Access or Tickets: Access',
                description:
                  'Explains capabilities for Conversational AI and dependency on other access permissions',
              }),
              feature: 'conversational_ai',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
          ],
        },
      },
      {
        type: 'permission_group',
        id: '9c75a72b-78f8-40db-b8a2-303e24d12127',
        attributes: {
          name: intl.formatMessage({
            id: 'GetPermissionsResponse.applicationAdministrationGroupName',
            defaultMessage: 'Application Administration',
            description:
              'Permission group title for Application Administration',
          }),
          permissions: [
            {
              id: '5fcdf68d-9f6a-4137-8c67-7e31222735c5',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.usersManageName',
                defaultMessage: 'Users: Manage',
                description: 'Permission name for managing users',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.usersManageDescription',
                defaultMessage:
                  'Create, edit, or deactivate users. Assign roles to users.',
                description: 'Explains capabilities for managing users',
              }),
              feature: 'users',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '1f98ae2b-0c4f-4c4a-ae9b-21582a5e1622',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.departmentsManageName',
                defaultMessage: 'Departments: Manage',
                description: 'Permission name for managing departments',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.departmentsManageDescription',
                defaultMessage: 'Create, view, edit, or delete departments.',
                description: 'Explains capabilities for managing departments',
              }),
              feature: 'departments',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: 'c978e2d8-7032-4e4e-9c20-98a79fc43f7d',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.rolesManageName',
                defaultMessage: 'Roles: Manage',
                description: 'Permission name for managing roles',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.rolesManageDescription',
                defaultMessage: 'Create, view, edit, or delete roles.',
                description: 'Explains capabilities for managing roles',
              }),
              feature: 'roles',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '607e2151-b480-4f19-918f-9e57ab9e7d39',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.emailIntakeManageName',
                defaultMessage: 'Email intake: Manage',
                description: 'Permission name for managing email intake',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.emailIntakeManageDescription',
                defaultMessage: 'Set up document import to Evisort via email.',
                description:
                  'Explains capabilities for configuring email intake',
              }),
              feature: 'email_intake',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            ...(!hasUnifiedFieldsM1
              ? [
                  {
                    id: 'e68f77b6-3e08-4e6f-9ed2-1ad6b1c8f927',
                    name: intl.formatMessage({
                      id: 'GetPermissionsResponse.dataFieldsManageName',
                      defaultMessage: 'Data fields: Manage',
                      description: 'Permission name for managing data fields',
                    }),
                    description: intl.formatMessage({
                      id: 'GetPermissionsResponse.dataFieldsManageDescription',
                      defaultMessage:
                        'Configure document data fields for your organization.',
                      description:
                        'Explains capabilities for configuring data fields',
                    }),
                    feature: 'data_fields',
                    premium: true,
                    relations: [
                      'feature_creator',
                      'feature_editor',
                      'feature_deleter',
                    ],
                  },
                ]
              : []),
            ...(hasUnifiedFieldsM1
              ? [
                  {
                    id: '02a2d5f4-e0c8-46aa-8d3f-1b3e6219df63',
                    name: intl.formatMessage({
                      id: 'GetPermissionsResponse.fieldManagerManageName',
                      defaultMessage: 'Field Manager: Manage',
                      description: 'Permission name for managing field manager',
                    }),
                    description: intl.formatMessage({
                      id: 'GetPermissionsResponse.fieldManagerManageDescription',
                      defaultMessage:
                        'Configure field manager settings for your organization.',
                      description:
                        'Explains capabilities for configuring field manager settings',
                    }),
                    feature: 'field_manager',
                    premium: true,
                    relations: [
                      'feature_creator',
                      'feature_editor',
                      'feature_deleter',
                    ],
                  },
                ]
              : []),
            {
              id: '050e16f8-725e-422e-a1f9-16f6af78b09c',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.integrationsManageName',
                defaultMessage: 'Integrations: Manage',
                description: 'Permission name for managing integrations',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.integrationsManageDescription',
                defaultMessage:
                  'Manage third-party integrations for Electronic Signature, CRM, and Document Management Systems (Storage).',
                description:
                  'Explains capabilities for managing third-party integrations',
              }),
              feature: 'integrations',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: 'a19a7f5c-028e-4e4b-a87e-30c2e78309bc',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.ssoManageName',
                defaultMessage: 'Single Sign-on (SSO): Manage',
                description: 'Permission name for managing SSO',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.ssoManageDescription',
                defaultMessage:
                  'Set up and manage single sign-on for your organization.',
                description:
                  'Explains capabilities for setting up and managing SSO',
              }),
              feature: 'sso',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '5c0a8528-7e43-4a86-8c6d-2e4e9e6df5f9',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.restorationHubManageName',
                defaultMessage: 'Restoration hub: Manage',
                description: 'Permission name for managing restoration hub',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.restorationHubManageDescription',
                defaultMessage:
                  'Restore deleted documents for your organization.',
                description:
                  'Explains capabilities for restoring deleted documents',
              }),
              feature: 'restoration_hub',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '7c72e1c6-8d0a-4e22-9c9e-10bb3ce6f4e6',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.moreSettingsManageName',
                defaultMessage: 'More settings: Manage',
                description:
                  'Permission name for managing miscellaneous settings',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.moreSettingsManageDescription',
                defaultMessage:
                  'Manage settings for Quick AI, folder access, and document metadata bulk upload.',
                description:
                  'Explains capabilities for managing various application settings',
              }),
              feature: 'more_settings',
              premium: true,
              relations: [
                'feature_creator',
                'feature_editor',
                'feature_deleter',
              ],
            },
            {
              id: '77d80dd0-f22c-45f9-a51b-7eed54369586',
              name: intl.formatMessage({
                id: 'GetPermissionsResponse.auditLogsExportName',
                defaultMessage: 'Audit Logs: Export',
                description: 'Permission name for exporting audit logs',
              }),
              description: intl.formatMessage({
                id: 'GetPermissionsResponse.auditLogsExportDescription',
                defaultMessage:
                  'View and export audit logs for your organization.',
                description:
                  'Explains capabilities for viewing and exporting audit logs',
              }),
              feature: 'audit_logs',
              premium: true,
              relations: ['feature_viewer'],
            },
          ],
        },
      },
    ],
  };
};

export default getPermissionsResponse;
